import React, { useState, useEffect, useCallback } from "react";
import Modal from "./components/Modal";
import articleService from "../../../service/article.service";
import StatusHandler from "../../../components/articles/StatusHandler";
import { useArticleManagement } from "../../../hooks/useArticleManagement";
import { useArticleNavigation } from "../../../hooks/useArticleNavigation";
import ArticleSearch from "../../../components/articles/ArticleSearch";
import ArticleTable from "../../../components/articles/ArticleTable";
import Pagination from "../../../components/articles/Pagination";
import "react-quill/dist/quill.snow.css";
import useUserRole from "./../../../utils/useUserRole";

const ArticleRefuse = () => {
  // Hook de navigation réutilisable
  const { navigateToDetails } = useArticleNavigation();
  const userRole = useUserRole();

  // Utilisation du hook partagé avec navigation personnalisée
  const {
    isLoading,
    error,
    searchTerm,
    currentItems,
    handleSearch,
    getArticleTitle,
    getArticleAuthor,
    getArticleDate,
    fetchArticles,
    currentPage,
    handlePageChange,
    itemsPerPage,
    handleItemsPerPageChange,
    indexOfFirstItem,
    indexOfLastItem,
    totalItems,
    totalPages,
    isModalOpen,
    setIsModalOpen,
    modalTitle,
    setModalTitle,
    modalSize,
    setModalSize,
    modalContent,
  } = useArticleManagement(() => articleService.getAllByStatus(2, 500, 1), null, {
    enableModal: true,
    enableEdit: false,
    customNavigationHandler: navigateToDetails,
  });

  // Formater une date ISO
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Non spécifiée";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  // Obtenir les noms des évaluateurs
  const getEvaluateursNames = useCallback((article) => {
    if (!article.reviewers || article.reviewers.length === 0) {
      return "Aucun évaluateur assigné";
    }
    return article.reviewers
      .map(
        (reviewer) =>
          `${reviewer.grade_rev || ""} ${reviewer.first_name || ""} ${reviewer.last_name || ""}`
      )
      .join(", ");
  }, []);

  // Callback de mise à jour de statut
  const handleStatusUpdate = useCallback(async ({ article, newStatus, comment }) => {
    console.log("🔄 Callback mise à jour statut article refusé:", {
      articleId: article.id_art,
      fromStatus: 2, // Refusé
      toStatus: newStatus,
      commentLength: comment.length,
      user: "HiQaody",
      timestamp: "2025-08-31 07:27:08",
    });

    try {
      await fetchArticles();
      setIsModalOpen(false);
      
      console.log("✅ Articles refusés rechargés après changement statut:", {
        newTotalItems: totalItems,
        user: "HiQaody",
        timestamp: "2025-08-31 07:27:08",
      });
    } catch (error) {
      console.error("❌ Erreur lors du rechargement des articles refusés:", {
        error: error.message,
        user: "HiQaody",
        timestamp: "2025-08-31 07:27:08",
      });
    }
  }, [fetchArticles, setIsModalOpen, totalItems]);

  // Configuration personnalisée pour ArticleRefuse
  const customStatusFilter = useCallback((status) => {
    // Exclure les statuts "publié" et "en révision"
    return status.id_stat !== 5 && status.id_stat !== 4;
  }, []);

  // Règles d'avertissement spécifiques pour ArticleRefuse
  const warningRules = {
    backToRemanement: {
      condition: (orig, new_stat) => 
        (orig === '7' || orig === 7) && (new_stat === '2' || new_stat === 2),
      title: '⚠️ Attention: Réhabilitation',
      description: 'Vous allez changer un article "refusé" vers "à remanier". Cette action donnera une nouvelle chance à l\'article.',
      config: {
        title: '🔄 Réhabilitation d\'article',
        text: 'Vous êtes sur le point de donner une nouvelle chance à cet article refusé. Cette action permettra à l\'auteur de le remanier.',
        icon: 'warning',
        confirmButtonText: 'Oui, réhabiliter l\'article'
      }
    },
    toPublishable: {
      condition: (orig, new_stat) => 
        (orig === '7' || orig === 7) && (new_stat === '3' || new_stat === 3),
      title: '🚀 Passage direct en publiable',
      description: 'Vous allez directement marquer un article refusé comme publiable. Action exceptionnelle.',
      config: {
        title: '🚀 Passage exceptionnel en publiable',
        text: 'Vous êtes sur le point de marquer directement un article refusé comme publiable. Cette action est exceptionnelle et nécessite une justification détaillée.',
        icon: 'warning',
        confirmButtonText: 'Oui, marquer comme publiable'
      }
    },
    maintainRejection: {
      condition: (orig, new_stat) => 
        (orig === '7' || orig === 7) && (new_stat === '7' || new_stat === 7),
      config: {
        title: '❌ Confirmation du refus',
        text: 'Vous confirmez le refus de cet article. Un nouveau commentaire sera ajouté à l\'historique.',
        icon: 'info'
      }
    }
  };

  // Suggestions de commentaires pour ArticleRefuse
  const suggestionComments = [
    "Après réévaluation, l'article peut être remanié avec les corrections suggérées",
    "Nouvelles données apportées qui justifient une révision du statut",
    "Erreur d'évaluation initiale, l'article mérite une seconde chance",
    "Contexte modifié, l'article devient pertinent pour publication",
    "Recommandations de l'équipe éditoriale pour réexamen",
    "Mise à jour des critères de publication, article maintenant éligible",
    "Refus maintenu - critères de qualité scientifique non atteints",
    "Article ne répond toujours pas aux standards de la revue"
  ];

  // Colonnes personnalisées pour le tableau
  const customColumns = [
    {
      header: "Évaluateurs",
      render: (article) => (
        <div className="text-sm text-gray-600">
          {getEvaluateursNames(article)}
        </div>
      ),
    },
    {
      header: "Date de soumission",
      render: (article) => (
        <div className="text-sm text-gray-600">
          {formatDate(article.date_soum_art)}
        </div>
      ),
    },
    {
      header: "Date de refus",
      render: (article) => (
        <div className="text-sm text-gray-600">
          {article.date_eval_art
            ? formatDate(article.date_eval_art)
            : "Non évalué"}
        </div>
      ),
    },
  ];

  // État de chargement
  if (isLoading) {
    console.log("⏳ Chargement des articles refusés:", {
      user: "HiQaody",
      timestamp: "2025-08-31 07:27:08",
    });
    
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-[#57A863]"></div>
      </div>
    );
  }

  // État d'erreur
  if (error) {
    console.error("❌ Erreur lors du chargement des articles refusés:", {
      error,
      user: "HiQaody",
      timestamp: "2025-08-31 07:27:08",
    });
    
    return (
      <div className="rounded-lg bg-red-50 p-4 text-center text-red-800">
        <p>{error}</p>
        <button
          onClick={fetchArticles}
          className="mt-4 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  console.log("🖼️ Rendu ArticleRefuse:", {
    totalItems,
    currentPage,
    userRole,
    hasItems: currentItems.length > 0,
    user: "HiQaody",
    timestamp: "2025-08-31 07:27:08",
  });

  return (
    <div className="container mx-auto px-4 py-6">
      <ArticleSearch
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        title="Articles refusés"
        subtitle={`${totalItems} article${totalItems !== 1 ? "s" : ""} refusé${totalItems !== 1 ? "s" : ""}`}
      />

      <StatusHandler
        onStatusUpdate={handleStatusUpdate}
        showConfirmation={true}
        customStatusFilter={customStatusFilter}
        warningRules={warningRules}
        minimumCommentLength={15} // Plus strict pour les articles refusés
        suggestionComments={suggestionComments}
      >
        {({ 
          initializeStatusAttribution, 
          selectedArticle: statusSelectedArticle,
          StatusSelector,
          CommentInput, 
          StatusMessages,
          handleSubmitStatus,
          isValid,
          isSubmitting: statusIsSubmitting 
        }) => (
          <>
            <div className="mb-6 overflow-hidden rounded-lg">
              <ArticleTable
                currentItems={currentItems}
                getArticleTitle={getArticleTitle}
                getArticleAuthor={getArticleAuthor}
                getArticleDate={getArticleDate}
                handleActionPrimary={userRole === "Administrateur" ? undefined : (article) => {
                  console.log("🎯 Attribution statut initiée pour article refusé:", {
                    articleId: article.id_art,
                    currentStatus: article.id_stat, // Devrait être 7 (refusé)
                    title: getArticleTitle(article),
                    user: "HiQaody",
                    timestamp: "2025-08-31 07:27:08",
                  });
                  initializeStatusAttribution(article);
                  setModalTitle("Attribuer des status");
                  setModalSize("2xl");
                  setIsModalOpen(true);
                }}
                handleViewDetails={navigateToDetails}
                showEditButton={false}
                showPrimaryButton={false}
                showStatusButton={userRole !== "Administrateur"}
                handleStatusArticle={(article) => {
                  console.log("📊 Ouverture modal statut depuis bouton:", {
                    articleId: article.id_art,
                    user: "HiQaody",
                    timestamp: "2025-08-31 07:27:08",
                  });
                  initializeStatusAttribution(article);
                  setModalTitle("Attribuer des status");
                  setModalSize("2xl");
                  setIsModalOpen(true);
                }}
                actionPrimaryText={userRole === "Administrateur" ? null : "Attribuer"}
                handleReloadData={fetchArticles}
                customColumns={customColumns}
                dateColumnLabel="date de refus"
              />

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                handleItemsPerPageChange={handleItemsPerPageChange}
                indexOfFirstItem={indexOfFirstItem}
                indexOfLastItem={indexOfLastItem}
                totalItems={totalItems}
              />
            </div>

            {/* Modal avec StatusHandler */}
            <Modal
              isOpen={isModalOpen}
              onClose={() => {
                console.log("🔄 Fermeture modal articles refusés:", {
                  user: "HiQaody",
                  timestamp: "2025-08-31 07:27:08",
                });
                setIsModalOpen(false);
              }}
              title={modalTitle}
              width={modalSize}
            >
              {modalTitle === "Attribuer des status" && statusSelectedArticle ? (
                <div className="bg-white">
                  {/* En-tête de la modal avec informations de l'article */}
                  <div className="border-b border-gray-200 px-6 py-4">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                          <svg
                            className="h-5 w-5 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-lg font-semibold text-gray-900">
                          {getArticleTitle(statusSelectedArticle)}
                        </h3>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                          <span>Par {getArticleAuthor(statusSelectedArticle)}</span>
                          <span>•</span>
                          <span>
                            Soumis le {formatDate(statusSelectedArticle?.date_soum_art)}
                          </span>
                          <span>•</span>
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                            Article refusé
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Informations sur les évaluateurs */}
                  {statusSelectedArticle.reviewers && statusSelectedArticle.reviewers.length > 0 && (
                    <div className="border-b border-gray-100 bg-gray-50 px-6 py-3">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="font-medium">Évaluateurs:</span>
                        <span>{getEvaluateursNames(statusSelectedArticle)}</span>
                      </div>
                    </div>
                  )}

                  {/* Corps de la modal */}
                  <div className="space-y-6 px-6 py-6">
                    {statusSelectedArticle.id_stat !== 5 ? (
                      <>
                        {/* Information contextuelle */}
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <svg
                                className="mt-0.5 h-5 w-5 text-amber-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <h5 className="text-sm font-medium text-amber-900">
                                Changement de statut d'article refusé
                              </h5>
                              <p className="mt-1 text-sm text-amber-700">
                                Cet article a été refusé. Vous pouvez le réhabiliter vers "à remanier" 
                                pour donner une nouvelle chance à l'auteur, ou confirmer le refus avec 
                                un nouveau commentaire.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Sélection du nouveau statut */}
                        {StatusSelector}

                        {/* Section commentaire avec longueur minimale plus stricte */}
                        {CommentInput}
                      </>
                    ) : (
                      // Article publié (cas rare)
                      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <svg
                              className="mt-0.5 h-5 w-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h5 className="text-sm font-medium text-green-900">
                              Article publié
                            </h5>
                            <p className="mt-1 text-sm text-green-700">
                              Cet article est maintenant publié et ne peut plus changer de statut.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Messages d'erreur et de succès */}
                    {StatusMessages}
                  </div>

                  {/* Footer avec boutons */}
                  <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
                    <button
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                      onClick={() => {
                        console.log("❌ Annulation changement statut article refusé:", {
                          user: "HiQaody",
                          timestamp: "2025-08-31 07:27:08",
                        });
                        setIsModalOpen(false);
                      }}
                      disabled={statusIsSubmitting}
                    >
                      Annuler
                    </button>
                    
                    {statusSelectedArticle.id_stat !== 5 && (
                      <button
                        className={`rounded-lg px-6 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          isValid && !statusIsSubmitting
                            ? "bg-[#57A863] hover:bg-[#4a9154] focus:ring-[#57A863] shadow-lg hover:shadow-xl"
                            : "cursor-not-allowed bg-gray-400 focus:ring-gray-400"
                        }`}
                        onClick={() => {
                          console.log("🚀 Soumission changement statut article refusé:", {
                            articleId: statusSelectedArticle.id_art,
                            newStatus: statusSelectedArticle.id_stat,
                            isValid,
                            user: "HiQaody",
                            timestamp: "2025-08-31 07:27:08",
                          });
                          handleSubmitStatus();
                        }}
                        disabled={!isValid || statusIsSubmitting}
                      >
                        {statusIsSubmitting ? (
                          <span className="flex items-center">
                            <svg
                              className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Attribution en cours...
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            Attribuer le statut
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                // Autres contenus de modal (détails article, etc.)
                modalContent
              )}
            </Modal>
          </>
        )}
      </StatusHandler>
    </div>
  );
};

export default ArticleRefuse;